import { clsx } from "clsx";
import { type CSSProperties, type PropsWithChildren, type ReactNode } from "react";
import { useHover } from "react-aria";
import {
  Button,
  Disclosure as ReactAriaDisclosure,
  DisclosurePanel,
  type DisclosureProps,
  Heading,
} from "react-aria-components";

import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { HStack } from "../Stack/HStack.tsx";
import css from "./Disclosure.module.css";

export interface AriaDisclosureProps extends Omit<DisclosureProps, "title"> {
  header: ReactNode;
  actions?: ReactNode[];
  headingClassName?: string;
  headingStyle?: CSSProperties;
  panelClassName?: string;
  panelStyle?: CSSProperties;
}

const TriggerIcon = ({ isExpanded, isFocusVisible }: { isExpanded: boolean; isFocusVisible: boolean }) => {
  const { hoverProps, isHovered } = useHover({});

  return (
    <div
      className={clsx(css.trigger_icon, {
        [css.hover]: isHovered,
        [css.focus]: isFocusVisible,
      })}
      {...hoverProps}
    >
      <SymbolIcon icon={isExpanded ? "keyboard_arrow_down" : "keyboard_arrow_right"} size={16} className={css.icon} />
    </div>
  );
};

export const Disclosure = ({
  header,
  actions,
  children,
  headingClassName,
  headingStyle,
  panelClassName,
  panelStyle,
  ...props
}: PropsWithChildren<AriaDisclosureProps>) => {
  if (header == null) {
    return null;
  }

  return (
    <ReactAriaDisclosure {...props}>
      {({ isExpanded }) => (
        <>
          <Heading style={{ all: "unset", display: "contents" }}>
            <HStack className={headingClassName} style={headingStyle}>
              <Button
                slot="trigger"
                className={css.trigger}
                data-testid="disclosure-trigger"
                data-e2e-label={isExpanded ? "Collapse" : "Expand"}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                {({ isFocusVisible }) => (
                  <>
                    <TriggerIcon isExpanded={isExpanded} isFocusVisible={isFocusVisible} />
                    {header}
                  </>
                )}
              </Button>
              {actions && actions.length > 0 && (
                <HStack spacing={8} data-testid={"disclosure-actions"}>
                  {actions}
                </HStack>
              )}
            </HStack>
          </Heading>
          <DisclosurePanel className={clsx(css.panel, panelClassName)} style={panelStyle}>
            {children}
          </DisclosurePanel>
        </>
      )}
    </ReactAriaDisclosure>
  );
};
